import React, {useEffect, useState} from "react";
import {useQuery, useQueryClient} from "react-query";
import {Link, useLocation, useNavigate, useParams} from "react-router-dom";
import {useRecoilValue, useSetRecoilState} from "recoil";
import * as examApi from "../../../api/educationApi/examApi";

import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
} from "@mui/material";
import {tokenSelector} from "../../../Recoil/Atom/authState";
import SubTitle from "../../../components/SubTitle";
import {SubMenuGroup} from "../../../styledComponents/StyledListContainer";

import {useDepartmentList} from "../../../hooks/useDepartmentList";
import {
  EDExam,
  includeExamParam,
  initialExamRankListParam,
} from "../../../models/serverModels/education/EDExam";
import MWSkeleton from "../../../components/MWSkeleton";
import {EQDepartment, initialDepartmentList} from "../../../models/serverModels/EQDepartment";
import {useCreateMutation} from "../../../hooks/useQueryHooks";
import {ResponseItem} from "../../../models/clientModels/interfaces/Response";
import {snackBarState, snackBarToggleState} from "../../../Recoil/Atom/snackBarState";
import {StyledNoneScrollTable, StyledNoneStickyTable} from "../../../styledComponents/StyledTable";

const ListContainer = styled(Box)(({theme}) => ({
  minWidth: "1500px",
}));
type stateType = {
  departments: string[];
};

const ExamRank = () => {
  const {id} = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as stateType;

  const queryClient = useQueryClient();

  const tokenState = useRecoilValue(tokenSelector);
  const setSnackBarState = useSetRecoilState(snackBarState);
  const setSnackBarToggle = useSetRecoilState(snackBarToggleState);

  const [filter, setFilter] = useState(initialExamRankListParam);
  const [filterDepartment, setFilterDepartment] = useState<EQDepartment[]>([initialDepartmentList]);

  const {departmentList} = useDepartmentList();

  const {
    data: examRank,
    isLoading,
    isFetching,
  } = useQuery(["ExamRank", [{...filter, token: tokenState, id}]], () =>
    examApi.ExamRank({...filter, token: tokenState, id: parseInt(id!)})
  );

  const {mutate: includeMutate} = useCreateMutation<ResponseItem<EDExam>, includeExamParam>({
    api: examApi.IncludeExam,
    options: {
      onSuccess: () => {
        setSnackBarToggle(true);
        setSnackBarState("update");
        navigate(-1);
        queryClient.invalidateQueries("ExamRank");
      },
      onError: () => {
        setSnackBarToggle(true);
        setSnackBarState("failed");
      },
    },
  });

  const departmentHandler = (event: SelectChangeEvent<number>) => {
    setFilter({
      ...filter,
      [event.target.name]: event.target.value,
    });
  };

  const departmentFilterHandler = (departmentData: stateType) => {
    if (departmentList?.items !== undefined) {
      let data = [];
      for (let i = 0; i < departmentData.departments.length; i++) {
        for (let j = 0; j < departmentList?.items.length; j++) {
          if (parseInt(departmentData.departments[i]) === departmentList.items[j].uid) {
            data.push(departmentList.items[j]);
          }
        }
      }
      setFilterDepartment(data);
    }
  };

  useEffect(() => {
    state.departments && departmentFilterHandler(state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ListContainer>
      <SubTitle subTitle="평가순위" />
      <SubMenuGroup>
        <FormControl>
          <InputLabel id="departmentUID">대상부서</InputLabel>
          <Select
            name="departmentUID"
            id="departmentUID"
            label="대상부서"
            size="small"
            value={filter?.departmentUID}
            onChange={departmentHandler}
            sx={{minWidth: "250px"}}
          >
            <MenuItem value={-1}>전체</MenuItem>
            {filterDepartment.map((item, index) => (
              <MenuItem key={item.uid} value={item.uid}>
                {item.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </SubMenuGroup>
      <StyledNoneScrollTable>
        <Table>
          <StyledNoneStickyTable>
            <TableRow>
              <TableCell align="center">#</TableCell>
              <TableCell align="center">평가일</TableCell>
              <TableCell align="center">최고점수</TableCell>
              <TableCell align="center">응시횟수</TableCell>
              <TableCell align="center">부서명</TableCell>
              <TableCell align="center">이름</TableCell>
              <TableCell align="center">비고</TableCell>
              <TableCell align="center">평가제외</TableCell>
            </TableRow>
          </StyledNoneStickyTable>
          <TableBody>
            {isLoading && isFetching ? (
              <MWSkeleton row={12} column={8} />
            ) : (
              examRank?.items.map((item, index) => (
                <TableRow key={item.userUID}>
                  <TableCell align="center">{index + 1}</TableCell>
                  <TableCell align="center">
                    <Link to={`/education/ExamScore/${item.uid}`}>
                      {new Date(item?.examDate).getFullYear() < 2000
                        ? "미응시"
                        : `${new Date(item?.examDate).getFullYear()}년 ${
                            new Date(item?.examDate).getMonth() + 1
                          }월 ${new Date(item?.examDate).getDate()}일 `}
                    </Link>
                  </TableCell>
                  <TableCell align="center">{item.score}</TableCell>
                  <TableCell align="center">{item.trialCount}</TableCell>
                  <TableCell align="center">{item.departmentName}</TableCell>
                  <TableCell align="center">{item.userName}</TableCell>
                  <TableCell align="center">{item.note}</TableCell>

                  <TableCell align="center">
                    {item.isExcluded ? (
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => {
                          includeMutate({token: tokenState, id: parseInt(id!), uid: item.userUID});
                        }}
                      >
                        평가포함
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={() => {
                          navigate(`/Education/ExcludeExam/${id}/${item.userUID}`);
                        }}
                      >
                        평가제외
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </StyledNoneScrollTable>
    </ListContainer>
  );
};

export default ExamRank;
